import { useState, useEffect, useCallback } from 'react';
import { Text, Button, Badge, Tab, TabList, Spinner } from '@fluentui/react-components';
import { ArrowLeftRegular, ClipboardRegular } from '@fluentui/react-icons';
import { listRegisters, type RegisterEntry, type RegisterType, type FactData, type WorkloadData, type DecisionData, type RiskData } from '../services/register-service';
import { useApiToken } from '../hooks/useApiToken';
import type { Project } from '../services/project-service';

const REGISTER_TABS: { key: RegisterType; label: string }[] = [
  { key: 'fact', label: 'Facts & Assumptions' },
  { key: 'workload', label: 'Workloads' },
  { key: 'decision', label: 'Decisions' },
  { key: 'risk', label: 'Risks' },
  { key: 'stakeholder', label: 'Stakeholders' },
  { key: 'skill', label: 'Skills' },
];

const FACT_STATUS_COLOUR: Record<string, 'success' | 'warning' | 'danger'> = {
  confirmed: 'success',
  assumed: 'warning',
  unknown: 'danger',
};

interface RegistersPageProps {
  project: Project;
  onBack: () => void;
}

export function RegistersPage({ project, onBack }: RegistersPageProps) {
  const [activeTab, setActiveTab] = useState<RegisterType>('fact');
  const [entries, setEntries] = useState<RegisterEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const getToken = useApiToken();

  const loadEntries = useCallback(async () => {
    if (!project.id) return;
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const items = await listRegisters(project.customerId, project.id, activeTab, token);
      setEntries(items);
    } catch (err) {
      console.error('Failed to load registers:', err);
    } finally {
      setLoading(false);
    }
  }, [project.id, project.customerId, activeTab, getToken]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  return (
    <div className="page-root page-root--scrollable">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title="Back to project" />
        <ClipboardRegular className="icon-title" />
        <Text size={500} weight="semibold">Registers — {project.name}</Text>
      </div>

      <div style={{ padding: '0 24px' }}>
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, d) => setActiveTab(d.value as RegisterType)}
          size="small"
          style={{ marginBottom: 16 }}
        >
          {REGISTER_TABS.map(tab => (
            <Tab key={tab.key} value={tab.key}>{tab.label}</Tab>
          ))}
        </TabList>

        {loading ? (
          <Spinner label="Loading..." size="small" />
        ) : entries.length === 0 ? (
          <Text style={{ color: '#616161', padding: 16 }}>
            No {activeTab} entries yet. Use the Project chat to add them, or they will be populated during the engagement.
          </Text>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {activeTab === 'fact' && <FactsTable entries={entries} />}
            {activeTab === 'workload' && <WorkloadsTable entries={entries} />}
            {activeTab === 'decision' && <DecisionsTable entries={entries} />}
            {activeTab === 'risk' && <RisksTable entries={entries} />}
            {activeTab === 'stakeholder' && <GenericTable entries={entries} fields={['name', 'role', 'organisation', 'email', 'raciRole']} />}
            {activeTab === 'skill' && <GenericTable entries={entries} fields={['personName', 'currentRole', 'status']} />}
          </div>
        )}
      </div>
    </div>
  );
}

function FactsTable({ entries }: { entries: RegisterEntry[] }) {
  return (
    <table className="data-table" style={{ width: '100%' }}>
      <thead>
        <tr>
          <th>Statement</th>
          <th>Category</th>
          <th>Status</th>
          <th>Blocking</th>
          <th>Source</th>
          <th>Confirmed By</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as unknown as FactData | undefined;
          if (!d) return null;
          return (
            <tr key={e.id}>
              <td>{d.statement}</td>
              <td>{d.category}</td>
              <td><Badge color={FACT_STATUS_COLOUR[d.status] ?? 'warning'} size="small">{d.status}</Badge></td>
              <td>{d.blocking ? <Badge color="danger" size="small">Blocking</Badge> : '—'}</td>
              <td style={{ fontSize: 12, color: '#616161' }}>{d.source}</td>
              <td>{d.confirmedBy ?? '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function WorkloadsTable({ entries }: { entries: RegisterEntry[] }) {
  return (
    <table className="data-table" style={{ width: '100%' }}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Type</th>
          <th>Target</th>
          <th>Strategy</th>
          <th>Status</th>
          <th>Est. Cost</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as unknown as WorkloadData | undefined;
          if (!d) return null;
          return (
            <tr key={e.id}>
              <td>
                <strong>{d.name}</strong>
                {d.components?.length > 0 && (
                  <div style={{ fontSize: 11, color: '#616161' }}>{d.components.join(', ')}</div>
                )}
              </td>
              <td>{d.type}</td>
              <td>
                <div>{d.targetAzureSku ?? d.targetService ?? '—'}</div>
                {d.targetSpec && <div style={{ fontSize: 11, color: '#616161' }}>{d.targetSpec}</div>}
              </td>
              <td>{d.migrationStrategy}</td>
              <td><Badge size="small" appearance="outline">{d.status}</Badge></td>
              <td>{d.estimatedMonthlyCost ? `$${d.estimatedMonthlyCost.toFixed(2)}/mo` : '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DecisionsTable({ entries }: { entries: RegisterEntry[] }) {
  return (
    <table className="data-table" style={{ width: '100%' }}>
      <thead>
        <tr>
          <th>Topic</th>
          <th>Decision</th>
          <th>Rationale</th>
          <th>Decided By</th>
          <th>Date</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as unknown as DecisionData | undefined;
          if (!d) return null;
          return (
            <tr key={e.id}>
              <td><strong>{d.topic}</strong></td>
              <td>{d.decision}</td>
              <td style={{ fontSize: 12, color: '#616161' }}>{d.rationale}</td>
              <td>{d.decider}</td>
              <td>{d.date}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function RisksTable({ entries }: { entries: RegisterEntry[] }) {
  return (
    <table className="data-table" style={{ width: '100%' }}>
      <thead>
        <tr>
          <th>Description</th>
          <th>Category</th>
          <th>L</th>
          <th>I</th>
          <th>Score</th>
          <th>Mitigation</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as unknown as RiskData | undefined;
          if (!d) return null;
          const score = d.likelihood * d.impact;
          return (
            <tr key={e.id}>
              <td>{d.description}</td>
              <td>{d.category}</td>
              <td>{d.likelihood}</td>
              <td>{d.impact}</td>
              <td><Badge color={score >= 15 ? 'danger' : score >= 8 ? 'warning' : 'success'} size="small">{score}</Badge></td>
              <td style={{ fontSize: 12 }}>{d.mitigation}</td>
              <td><Badge size="small" appearance="outline">{d.status}</Badge></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function GenericTable({ entries, fields }: { entries: RegisterEntry[]; fields: string[] }) {
  return (
    <table className="data-table" style={{ width: '100%' }}>
      <thead>
        <tr>
          {fields.map(f => <th key={f}>{f.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</th>)}
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as Record<string, unknown> | undefined;
          if (!d) return null;
          return (
            <tr key={e.id}>
              {fields.map(f => <td key={f}>{String(d[f] ?? '—')}</td>)}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
